import type { TabsProps } from 'antd'
import { createElement } from 'react'
import dayjs from 'dayjs'
import { getLeadVkLinkFormValue } from '../../../utils/leadFields'
import type { CrmLeadTaskCounts } from '@/api/types/crm/crmLeadTasks.types'
import type { LeadProfileLead } from '../LeadProfileModal.types'
import {
  EMPTY_CRM_LEAD_TASK_COUNTS,
  normalizeCrmLeadTaskCounts,
} from '../leadTaskCounts.utils'
import CrmLeadTaskCountsBadges from '../../CrmLeadTaskCountsBadges/CrmLeadTaskCountsBadges'

export const SKIP_LEAD_PROFILE_FIELDS = ['STATUS', 'DATEL', 'DATEZ'] as const

export const LEAD_PROFILE_TABS: TabsProps['items'] = [
  { label: 'О заявке', key: '1' },
  { label: 'Покупки', key: '2' },
  { label: 'История изменений', key: '3' },
  { label: 'UTM метки', key: '4' },
  { label: 'Задачи', key: '5' },
]

function renderTasksTabLabel(
  multipleDealsEnabled: boolean,
  taskCounts: CrmLeadTaskCounts,
) {
  return createElement(
    'span',
    { className: 'lead-profile-tab-tasks-label' },
    'Задачи',
    createElement(CrmLeadTaskCountsBadges, { taskCounts }),
    multipleDealsEnabled
      ? createElement(
          'span',
          { className: 'lead-profile-tab-scope-hint' },
          ' по сделке',
        )
      : null,
  )
}

/** Вкладки карточки; при сделках ON у UTM/Задач — пометка «по сделке». */
export function getLeadProfileTabs(
  multipleDealsEnabled: boolean,
  taskCounts: CrmLeadTaskCounts = EMPTY_CRM_LEAD_TASK_COUNTS,
): TabsProps['items'] {
  const normalizedCounts = normalizeCrmLeadTaskCounts(taskCounts)

  return (LEAD_PROFILE_TABS ?? []).map((tab) => {
    if (!tab) return tab

    if (tab.key === '5') {
      return {
        ...tab,
        label: renderTasksTabLabel(multipleDealsEnabled, normalizedCounts),
      }
    }

    if (!multipleDealsEnabled || tab.key !== '4') return tab

    return {
      ...tab,
      label: createElement(
        'span',
        null,
        'UTM метки',
        createElement(
          'span',
          { className: 'lead-profile-tab-scope-hint' },
          ' по сделке',
        ),
      ),
    }
  })
}

export function buildFormDataFromLead(
  leadSource: LeadProfileLead | undefined,
): Record<string, string> {
  const defaultData =
    leadSource?.fields
      ?.filter(
        (field) =>
          !(SKIP_LEAD_PROFILE_FIELDS as readonly string[]).includes(
            field.unique_name,
          ),
      )
      .reduce((acc, field) => {
        acc[field.unique_name] = String(field.value ?? '')
        return acc
      }, {} as Record<string, string>) || {}

  const vkLink = getLeadVkLinkFormValue(leadSource)
  if (vkLink) {
    defaultData.VK_LINK = vkLink
  }

  return defaultData
}

/** MySQL DATETIME/TIMESTAMP: `YYYY-MM-DD HH:mm:ss`, без ISO (T/Z/мс). */
export function toNextContactApiValue(
  raw: string | null | undefined,
): string | null {
  if (raw == null || String(raw).trim() === '') return null
  const d = dayjs(String(raw).trim())
  return d.isValid() ? d.format('YYYY-MM-DD HH:mm:ss') : null
}

export function getLeadProfileFieldsFingerprint(
  lead: LeadProfileLead | undefined,
): string {
  return (
    lead?.fields
      ?.filter(
        (field) =>
          !(SKIP_LEAD_PROFILE_FIELDS as readonly string[]).includes(
            field.unique_name,
          ),
      )
      .map(
        (field) => `${field.unique_name}\u0000${String(field.value ?? '')}`,
      )
      .sort()
      .join('\u0001') ?? ''
  )
}
